import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { AdminTable, type AdminTableColumn } from "@/components/ui/admin-table";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ModifierForm } from "@/features/strateg-cenowy/components/modifier-form";
import {
	createModifier,
	deleteModifier,
	fetchModifiers,
	updateModifier,
} from "@/features/strateg-cenowy/strateg-api";
import type {
	CreateModifierRequest,
	ModifierType,
	PriceModifierItem,
} from "@/features/strateg-cenowy/types";
import { useTenantSwitcher } from "@/features/tenant-switcher";
import { statusClasses } from "@/lib/colors";
import { ensureElementVisible } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

// ─── Tab config ─────────────────────────────────────────────────────────────

const TABS: { value: ModifierType; label: string }[] = [
	{ value: "premium", label: "Premium Brands" },
	{ value: "seasonal", label: "Sezonowe" },
	{ value: "tag", label: "Tagi reczne" },
	{ value: "trend", label: "Trendy" },
];

const MONTH_NAMES = [
	"",
	"Sty",
	"Lut",
	"Mar",
	"Kwi",
	"Maj",
	"Cze",
	"Lip",
	"Sie",
	"Wrz",
	"Paz",
	"Lis",
	"Gru",
];

// ─── Columns ────────────────────────────────────────────────────────────────

function buildColumns(
	activeTab: ModifierType,
	onEdit: (item: PriceModifierItem) => void,
	onDelete: (id: number) => void,
): AdminTableColumn<PriceModifierItem>[] {
	const cols: AdminTableColumn<PriceModifierItem>[] = [
		{
			key: "label",
			header: "Nazwa",
			render: (row) => <span className="font-medium text-sm">{row.label}</span>,
		},
		{
			key: "product",
			header: "Produkt / BLOZ",
			width: "120px",
			render: (row) =>
				row.bloz07 ? (
					<span className="font-mono text-xs" title={row.productName ?? row.bloz07}>
						{row.productName
							? `${row.productName.slice(0, 15)}${row.productName.length > 15 ? "..." : ""}`
							: row.bloz07}
					</span>
				) : (
					<span className="text-xs text-muted-foreground italic">Globalny</span>
				),
		},
		{
			key: "coefficient",
			header: "Wspolczynnik",
			width: "90px",
			// Miara na liscie telefonu (render bez `text-right`, heurystyka jej nie widzi).
			numeric: true,
			render: (row) => {
				const pct = Math.round((row.coefficient - 1) * 100);
				return (
					<span className={cn("font-mono text-xs", pct > 0 && "text-status-error-text")}>
						{pct > 0 ? `+${pct}%` : `${pct}%`}
					</span>
				);
			},
		},
	];

	if (activeTab === "seasonal") {
		cols.push({
			key: "period",
			header: "Okres",
			width: "120px",
			render: (row) =>
				row.monthFrom && row.monthUntil ? (
					<span className="text-xs">
						{MONTH_NAMES[row.monthFrom]} — {MONTH_NAMES[row.monthUntil]}
					</span>
				) : (
					<span className="text-xs text-muted-foreground">—</span>
				),
		});
	}

	cols.push(
		{
			key: "active",
			header: "Status",
			width: "80px",
			render: (row) => (
				<span
					className={cn(
						"inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border",
						row.active ? statusClasses.success : statusClasses.error,
					)}
				>
					{row.active ? "Aktywny" : "Nieaktywny"}
				</span>
			),
		},
		{
			key: "actions",
			header: "",
			width: "80px",
			render: (row) => (
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0 max-md:size-9"
						aria-label="Edytuj modyfikator"
						onClick={() => onEdit(row)}
					>
						<Pencil className="size-3.5" />
					</Button>
					{row.active && (
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0 text-status-error-text hover:text-status-error-text max-md:size-9"
							aria-label="Usun modyfikator"
							onClick={() => onDelete(row.id)}
						>
							<Trash2 className="size-3.5" />
						</Button>
					)}
				</div>
			),
		},
	);

	return cols;
}

// ─── Page ───────────────────────────────────────────────────────────────────

function ModyfikatoryPage() {
	const queryClient = useQueryClient();
	const { selectedTenantId } = useTenantSwitcher();
	const [activeTab, setActiveTab] = useState<ModifierType>("premium");
	const [formOpen, setFormOpen] = useState(false);
	const [editItem, setEditItem] = useState<PriceModifierItem | null>(null);

	const { data: modifiers = [], isLoading } = useQuery({
		queryKey: ["strateg-modifiers", selectedTenantId, activeTab],
		queryFn: () => fetchModifiers(activeTab),
	});

	const createMut = useMutation({
		mutationFn: (data: CreateModifierRequest) => createModifier(data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["strateg-modifiers", selectedTenantId, activeTab],
			});
			setFormOpen(false);
			setEditItem(null);
		},
	});

	const updateMut = useMutation({
		mutationFn: ({ id, data }: { id: number; data: CreateModifierRequest }) =>
			updateModifier(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["strateg-modifiers", selectedTenantId, activeTab],
			});
			setFormOpen(false);
			setEditItem(null);
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: number) => deleteModifier(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["strateg-modifiers", selectedTenantId, activeTab],
			});
		},
	});

	const handleEdit = useCallback((item: PriceModifierItem) => {
		setEditItem(item);
		setFormOpen(true);
	}, []);

	const handleDelete = useCallback(
		(id: number) => {
			deleteMut.mutate(id);
		},
		[deleteMut],
	);

	const handleSave = (data: CreateModifierRequest) => {
		if (editItem) {
			updateMut.mutate({ id: editItem.id, data });
		} else {
			createMut.mutate(data);
		}
	};

	const handleAddNew = () => {
		setEditItem(null);
		setFormOpen(true);
	};

	const columns = useMemo(
		() => buildColumns(activeTab, handleEdit, handleDelete),
		[activeTab, handleEdit, handleDelete],
	);

	return (
		<div className="flex flex-col gap-4 p-3 md:p-4">
			<PageHeader title="Modyfikatory cenowe" />

			<Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ModifierType)}>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
					<div className="-mx-3 px-3 overflow-x-auto md:mx-0 md:px-0 md:overflow-visible">
						<TabsList className="w-max">
							{TABS.map((tab) => (
								<TabsTrigger key={tab.value} value={tab.value}>
									{tab.label}
								</TabsTrigger>
							))}
						</TabsList>
					</div>
					<Button
						size="sm"
						className="bg-primary hover:bg-primary/90 text-primary-foreground self-start md:self-auto max-md:h-9"
						onClick={handleAddNew}
					>
						<Plus className="size-4 mr-1" />
						Dodaj modyfikator
					</Button>
				</div>

				{TABS.map((tab) => (
					<TabsContent key={tab.value} value={tab.value}>
						<div className="rounded-lg border bg-card">
							<AdminTable
								tableId={`strateg-cenowy-modyfikatory-${tab.value}`}
								columns={columns}
								data={modifiers}
								keyField="id"
								emptyMessage={isLoading ? "Ladowanie..." : "Brak modyfikatorow w tej kategorii."}
							/>
						</div>
					</TabsContent>
				))}
			</Tabs>

			<ModifierForm
				open={formOpen}
				onOpenChange={setFormOpen}
				modifierType={activeTab}
				editItem={editItem}
				onSave={handleSave}
				isPending={createMut.isPending || updateMut.isPending}
			/>
		</div>
	);
}

export const Route = createFileRoute("/_auth/strateg-cenowy/modyfikatory")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.strateg-cenowy.modyfikatory",
			location.pathname,
		);
	},
	component: ModyfikatoryPage,
});
